import { useEffect, useRef, useState } from 'react';
import { CareProfileResponse } from 'data-types';
import { MainTracker } from '../../../../../PixelTrackers/MainTracker';
import { TOUR_STEPS, TourStepConfig, isTourSeen, markTourSeen } from './careProfileTourConfig';

export type TourPhase = 'idle' | 'welcome' | 'step' | 'done';
type TourSource = 'auto' | 'replay';

interface UseCareProfileTourParams {
	customerId: string | null;
	careProfile: CareProfileResponse | null;
	isBlocked: boolean;
}

const trackTourEvent = (eventName: string, step: number, source: TourSource): void => {
	MainTracker.track('custom', eventName, { step, source });
};

export const useCareProfileTour = ({ customerId, careProfile, isBlocked }: UseCareProfileTourParams) => {
	const [phase, setPhase] = useState<TourPhase>('idle');
	const [stepIndex, setStepIndex] = useState<number>(0);
	const [activeSteps, setActiveSteps] = useState<TourStepConfig[]>([]);
	const sourceRef = useRef<TourSource>('auto');
	const hasAutoStartedRef = useRef<boolean>(false);

	useEffect(() => {
		if (!careProfile) {
			setActiveSteps([]);
			return;
		}

		let cancelled = false;
		const computeActiveSteps = (retryCount = 0): void => {
			if (cancelled) return;
			const eligibleSteps = TOUR_STEPS.filter((step) => {
				if (step.requires && !step.requires(careProfile)) return false;
				return true;
			});
			const allAnchorsMounted = eligibleSteps.every((step) => !step.anchorId || document.getElementById(step.anchorId));
			if (!allAnchorsMounted && retryCount < 10) {
				setTimeout(() => computeActiveSteps(retryCount + 1), 250);
				return;
			}
			setActiveSteps(eligibleSteps.filter((step) => !step.anchorId || document.getElementById(step.anchorId)));
		};
		computeActiveSteps();

		return () => {
			cancelled = true;
		};
	}, [careProfile]);

	// Register the Care+ super-properties as soon as an enrolled profile loads,
	// so tier/customer_status ride along on every tour event that follows.
	useEffect(() => {
		if (!careProfile || !careProfile.enrolled) return;
		MainTracker.superProperties({
			tier: careProfile.tierFlags?.tier ?? null,
			customer_status: careProfile.plan?.status ?? null,
		});
	}, [careProfile]);

	useEffect(() => {
		if (hasAutoStartedRef.current) return;
		if (!customerId || !careProfile) return;
		if (!careProfile.enrolled) return;
		if (careProfile.dashboardState !== 'active') return;
		if (isBlocked) return;
		if (isTourSeen(customerId)) return;

		hasAutoStartedRef.current = true;
		sourceRef.current = 'auto';
		setPhase('welcome');
		setStepIndex(0);
		trackTourEvent('Care+ Tour Started', 0, 'auto');
	}, [customerId, careProfile, isBlocked]);

	useEffect(() => {
		if (phase !== 'welcome') return;
		trackTourEvent('Care+ Tour Step Viewed', 0, sourceRef.current);
	}, [phase]);

	useEffect(() => {
		if (phase !== 'step') return;
		trackTourEvent('Care+ Tour Step Viewed', stepIndex + 1, sourceRef.current);
	}, [phase, stepIndex]);

	const start = (): void => {
		if (activeSteps.length === 0) return;
		setStepIndex(0);
		setPhase('step');
	};

	const next = (): void => {
		if (stepIndex >= activeSteps.length - 1) {
			complete();
			return;
		}
		setStepIndex((current) => current + 1);
	};

	const back = (): void => {
		setStepIndex((current) => Math.max(0, current - 1));
	};

	const skip = (): void => {
		const step = phase === 'welcome' ? 0 : stepIndex + 1;
		if (customerId) markTourSeen(customerId);
		trackTourEvent('Care+ Tour Skipped', step, sourceRef.current);
		setPhase('done');
	};

	const complete = (): void => {
		if (customerId) markTourSeen(customerId);
		trackTourEvent('Care+ Tour Completed', activeSteps.length, sourceRef.current);
		setPhase('done');
	};

	return {
		phase,
		stepIndex,
		activeSteps,
		currentStep: phase === 'step' ? activeSteps[stepIndex] ?? null : null,
		start,
		next,
		back,
		skip,
		complete,
	};
};
